import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { catchError, defer, map, of, shareReplay, startWith, tap } from 'rxjs';
import { ICurrentUserService } from '../../services/currentUserService/icurrentuser.service';
import { User, UserClient } from '../../services/nswag/nswagclient';
import { TelemetryService } from '../../services/telemetry.service';

type UserLookup = {
    status: string;
    user: User | null;
    errorMessage: string | null;
};

@Component({
    selector: 'app-userwithcompany',
    standalone: true,
    imports: [AsyncPipe, RouterModule],
    templateUrl: './userwithcompany.component.html',
    styleUrls: ['./userwithcompany.component.css']
})
export class UserwithcompanyComponent {
    private readonly _currentUserService = inject(ICurrentUserService);
    private readonly _msalService = inject(MsalService);
    private readonly _telemetryService = inject(TelemetryService);
    private readonly _userClient = inject(UserClient);
    private readonly _router = inject(Router);

    readonly allAccounts = signal<AccountInfo[]>(this._msalService.instance.getAllAccounts());
    readonly account = signal<AccountInfo | null>(this.getAccount());
    readonly currentUrl = signal(this.getCurrentUrl());
    readonly authRedirectFragmentWasPresent = signal(this.isAuthRedirectFragment(window.location.hash));
    readonly accountName = computed(() => this.account()?.name || 'Not available');
    readonly accountEmail = computed(() => this._currentUserService.getUserEmail() || this.getAccountEmail(this.account()) || 'Not available');
    readonly accountId = computed(() => this.account()?.localAccountId || this.account()?.homeAccountId || 'Not available');
    readonly tenantId = computed(() => this.account()?.tenantId || 'Not available');

    readonly userLookup$ = defer(() => {
        const email = this.accountEmail();
        if (!email || email === 'Not available') {
            return of<UserLookup>({ status: 'No sign-in email was available for the OpsViewer user lookup.', user: null, errorMessage: null }).pipe(
                tap(lookup => this.logNoPermissionsTelemetry(lookup))
            );
        }

        return this._userClient.getByEmail(email).pipe(
            map(user => ({ status: 'OpsViewer user record found.', user, errorMessage: null })),
            catchError(error => of<UserLookup>({
                status: 'OpsViewer user record could not be loaded.',
                user: null,
                errorMessage: this.getErrorMessage(error)
            })),
            tap(lookup => this.logNoPermissionsTelemetry(lookup)),
            tap(lookup => this.redirectIfUserHasAccess(lookup)),
            startWith({ status: 'Loading OpsViewer user record...', user: null, errorMessage: null })
        );
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    constructor() {
        this.clearStaleAuthRedirectFragment();
    }

    private getAccount(): AccountInfo | null {
        const accounts = this.allAccounts();
        return this._currentUserService.getUser()
            ?? this._msalService.instance.getActiveAccount()
            ?? accounts[0]
            ?? null;
    }

    private getAccountEmail(account: AccountInfo | null): string | null {
        if (!account) {
            return null;
        }

        const claims = account.idTokenClaims as Record<string, any> || {};
        return (Array.isArray(claims['emails']) && claims['emails'][0])
            || claims['email']
            || claims['preferred_username']
            || claims['upn']
            || claims['login_hint']
            || claims['signInNames.emailAddress']
            || claims['signInNames.email']
            || claims['signInNames']?.emailAddress
            || account.username
            || null;
    }

    private clearStaleAuthRedirectFragment(): void {
        if (!this.authRedirectFragmentWasPresent()) {
            return;
        }

        const cleanedUrl = `${window.location.pathname}${window.location.search}` || '/';
        window.history.replaceState(window.history.state, document.title, cleanedUrl);
        this.currentUrl.set(this.getCurrentUrl());
    }

    private isAuthRedirectFragment(hash: string): boolean {
        const normalizedHash = hash.toLowerCase();
        return normalizedHash.startsWith('#state=')
            || normalizedHash.startsWith('#code=')
            || normalizedHash.startsWith('#error=')
            || normalizedHash.includes('&state=')
            || normalizedHash.includes('&code=');
    }

    private getCurrentUrl(): string {
        return `${window.location.origin}${window.location.pathname}${window.location.search}`;
    }

    private logNoPermissionsTelemetry(lookup: UserLookup): void {
        const properties = this.getTelemetryProperties(lookup);
        this._telemetryService.trackEvent({ name: 'NoPermissionsShown' }, properties);
        this._telemetryService.trackTrace({ message: 'No permissions screen shown', severityLevel: SeverityLevel.Warning }, properties);
    }

    private redirectIfUserHasAccess(lookup: UserLookup): void {
        const u = lookup.user;
        if (u && (u.isGlobalAdmin || u.hasAllCompany || (u.companyRoles && u.companyRoles.length > 0))) {
            this._telemetryService.trackTrace({
                message: 'No permissions screen: lookup found user with access; redirecting to home.',
                severityLevel: SeverityLevel.Information
            }, { userCompanyRoleCount: String(u.companyRoles?.length ?? 0) });
            this._router.navigate(['/']);
        }
    }

    private getTelemetryProperties(lookup: UserLookup): Record<string, string> {
        return {
            accountName: this.accountName(),
            accountEmail: this.accountEmail(),
            accountId: this.accountId(),
            tenantId: this.tenantId(),
            currentPage: this.currentUrl(),
            authRedirectFragmentWasPresent: String(this.authRedirectFragmentWasPresent()),
            activeAccountPresent: String(!!this._msalService.instance.getActiveAccount()),
            allAccountCount: String(this.allAccounts().length),
            allAccountUsernames: this.allAccounts().map(account => account.username || 'No username').join(','),
            allAccountEnvironments: this.allAccounts().map(account => account.environment || 'No environment').join(','),
            selectedAccountClaimKeys: this.getClaimKeys(this.account()).join(','),
            selectedAccountEmailCandidates: this.getEmailCandidates(this.account()).join(','),
            lookupStatus: lookup.status,
            lookupErrorMessage: lookup.errorMessage ?? '',
            userIsGlobalAdmin: lookup.user ? String(lookup.user.isGlobalAdmin) : '',
            userHasAllCompany: lookup.user ? String(lookup.user.hasAllCompany) : '',
            userCompanyRoleCount: String(lookup.user?.companyRoles?.length ?? 0),
            userCompanyRoles: (lookup.user?.companyRoles ?? [])
                .map(role => `${role.role || 'Role not set'}:${role.companyId || 'Company not set'}`)
                .join(',')
        };
    }

    private getClaimKeys(account: AccountInfo | null): string[] {
        return Object.keys((account?.idTokenClaims as Record<string, unknown>) ?? {});
    }

    private getEmailCandidates(account: AccountInfo | null): string[] {
        if (!account) {
            return [];
        }

        const claims = account.idTokenClaims as Record<string, any> || {};
        return [
            ...(Array.isArray(claims['emails']) ? claims['emails'] : []),
            claims['email'],
            claims['preferred_username'],
            claims['upn'],
            claims['login_hint'],
            claims['signInNames.emailAddress'],
            claims['signInNames.email'],
            claims['signInNames']?.emailAddress,
            account.username
        ].filter((value): value is string => typeof value === 'string' && value.length > 0);
    }

    private getErrorMessage(error: unknown): string {
        if (typeof error === 'object' && error && 'message' in error) {
            return String((error as { message?: unknown }).message ?? 'Unknown error');
        }

        return String(error || 'Unknown error');
    }
}

